import React, { forwardRef, RefObject, useCallback, useEffect, useImperativeHandle, useRef } from "react";
import { useIntl } from "react-intl";
import { LOCALE_LABELS } from "checkout/ts/locale/labels";
import classnames from "classnames";
import { INTL_TEL_INPUT_UTILS_URL } from "common/constants";
import { Countries } from "common/locale/constants";
import intlTelInput, { Iti, SomeOptions } from "intl-tel-input";
import { noop } from "lodash";
import { PhoneNumber } from "univapay-node";

type Iti = ReturnType<typeof intlTelInput>;

type ItiProps = {
    value?: PhoneNumber;
    defaultValue?: PhoneNumber;
    onChange?: (value: PhoneNumber) => void;
    initOptions?: SomeOptions;
    inputProps?: Partial<React.InputHTMLAttributes<HTMLInputElement>>;
    disabled?: boolean;
    tabIndex?: number;
    className?: string;
    style?: React.CSSProperties;
    forcedCountryCode?: string;
};

export type IntlTelInputRef = {
    getInstance: () => Iti | null;
    getInput: () => HTMLInputElement | null;
};

const getLocalNumber = (ref: RefObject<Iti>) => ref.current?.telInput?.value || "";
const getCountryCode = (ref: RefObject<Iti>) => {
    const { areaCodes, dialCode = "" } = ref.current?.getSelectedCountryData();
    return areaCodes?.length ? `${dialCode}${areaCodes[0] || ""}` : dialCode;
};
const getCountryISOFromCountryCode = (countryCode: string) =>
    Object.values(Countries).find(({ dialCode }) => dialCode === countryCode)?.iso;

export const IntlTelInput = forwardRef(function IntlTelInput(
    {
        value,
        defaultValue,
        tabIndex,
        onChange = noop,
        className,
        style,
        initOptions = {},
        inputProps = {},
        disabled = false,
        forcedCountryCode,
    }: ItiProps,
    ref: React.ForwardedRef<IntlTelInputRef>
) {
    const { formatMessage } = useIntl();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const itiRef = useRef<Iti | null>(null);
    useImperativeHandle(ref, () => ({ getInstance: () => itiRef.current, getInput: () => inputRef.current }));

    /**
     * Initialise the `intlTelInput` component library with the reference of the html input field `inputRef`.
     * Pass in the optional `countryCode` during initialisation.
     * `localNumber` is not passed in explicitly, instead the previously mentioned `inputRef` is passed, and the
     * `intlTelInput` library will extend the functionality of this input. Event listeners on the `inputRef` will trigger
     * `onChange` events, emitting these values stored and managed in the `itiRef` in the form of a `PhoneNumber` object.
     */
    const initializeIntlTelInput = (countryCode: string) => {
        itiRef.current = intlTelInput(inputRef.current, {
            initOptions,
            ...{
                showSelectedDialCode: true,
                utilsScript: INTL_TEL_INPUT_UTILS_URL,
                i18n: {
                    ...Object.keys(Countries).reduce((acc, key) => {
                        acc[key.toLocaleLowerCase()] = formatMessage({ id: Countries[key].name });
                        return acc;
                    }, {}),
                    searchPlaceholder: formatMessage({ id: LOCALE_LABELS.COMMON_INPUT_SELECT_PLACEHOLDER }),
                },
                initialCountry: getCountryISOFromCountryCode(countryCode),
            },
        });
    };

    // Ensure the widget is updated when the inline form starts hidden
    useEffect(() => {
        if (!inputRef.current || inputRef.current?.clientWidth !== 0) {
            return;
        }

        const resizeObserver = new ResizeObserver(
            () =>
                (inputRef.current.style.paddingLeft = `${
                    document.querySelectorAll(".iti__flag-container")[0].clientWidth + 6
                }px`)
        );
        resizeObserver.observe(inputRef.current);

        return () => resizeObserver.disconnect();
    }, [inputRef.current?.clientWidth]);

    /**
     * The state value for this form field can be changed via the widget's update function. Because our input element
     * is an uncontrolled element, we'll need to manually update the text value of the input element and the `countryCode` stored in the
     * `intlTelInput` via references to reflect these changes.
     */
    useEffect(() => {
        /**
         * Because this will run on all updates to `value` (i.e all changes), we want to short circuit
         * and only manually update the input and country when there's a mismatch between the value in the redux store
         * and what the input is currently displaying - indicating the widget's update function execution.
         */
        if (inputRef.current?.value === value?.localNumber) {
            return;
        }
        const newCountryToFind = forcedCountryCode ?? value?.countryCode ?? "81";
        itiRef.current?.setCountry(getCountryISOFromCountryCode(newCountryToFind));
        inputRef.current.value = value.localNumber ?? "";
    }, [value]);

    useEffect(() => {
        const initialCountryCodeToFind = forcedCountryCode ?? defaultValue?.countryCode ?? "81";
        initializeIntlTelInput(initialCountryCodeToFind);
        return () => itiRef.current?.destroy();
    }, []);

    /**
     * All actions against the `inputRef` (input/paste) or the `itiRef` (country code change) will trigger
     * an update to the formatted PhoneNumber object this `intlTelInput` component wrapper is in charge of controlling.
     * Below are the handlers and useEffects to control this.
     */
    const handleUpdate = () => {
        const localNumber: string = getLocalNumber(itiRef);
        const countryCode: string = getCountryCode(itiRef);
        onChange({ localNumber, countryCode });
    };

    const handlePaste = useCallback(() => setTimeout(handleUpdate), [handleUpdate]);

    useEffect(() => {
        const inputRefCurrent = inputRef.current;
        if (inputRefCurrent) {
            inputRefCurrent.addEventListener("countrychange", handleUpdate);
            inputRefCurrent.addEventListener("paste", handlePaste);
        }

        return () => {
            if (inputRefCurrent) {
                inputRefCurrent.removeEventListener("countrychange", handleUpdate);
                inputRefCurrent.removeEventListener("paste", handlePaste);
            }
        };
    }, [handleUpdate, handlePaste]);

    return (
        <input
            defaultValue={defaultValue?.localNumber}
            type="tel"
            className={classnames(className, "intl-tel-input form-control")}
            ref={inputRef}
            onInput={handleUpdate}
            disabled={disabled}
            tabIndex={tabIndex}
            style={style}
            {...inputProps}
        />
    );
});
